import { Eye, MessageSquareQuote, Brain, Target, ListChecks, AlertTriangle, FileCheck2, Microscope } from "lucide-react";
import { LevelBadge } from "@/components/Common";
import { fmtDate } from "@/lib/api";
import { Block, List, Tag } from "@/components/analysis/resultUi";
import { DoNow, Management, AnamnesisFactors } from "@/components/analysis/ResultPlan";
import { Phyto, Safety } from "@/components/analysis/ResultPhyto";

const FURTHER = {
  foto: "Altre fotografie", professionista: "Valutazione professionale", osservazione_diretta: "Osservazione diretta",
  microscopia: "Microscopia", analisi_terreno: "Analisi del terreno", analisi_vegetale: "Analisi vegetale", laboratorio: "Analisi di laboratorio",
};

export default function ResultCard({ result, species }) {
  const r = result;
  return (
    <div className="space-y-4" data-testid="result-card">
      <div className="rounded-2xl bg-[#0D2517] p-5 text-[#F7F4EE]">
        <p className="eyebrow !text-[#C9A96E]">Scheda di analisi preliminare</p>
        <p className="font-serif text-2xl">{species?.common_name} <span className="botanical text-[#C9A96E]">{species?.botanical_name}</span></p>
        {r.overall_confidence ? (
          <div className="mt-3 space-y-2">
            <span className="inline-block rounded-full bg-white px-1 py-1"><LevelBadge level={r.overall_confidence} label="Attendibilità complessiva" testid="result-confidence" /></span>
            {r.main_cause && <p className="text-xs" data-testid="result-main-confidence">Causa principale: <strong>{r.main_cause.name}</strong> — Attendibilità {r.confidence.charAt(0).toUpperCase() + r.confidence.slice(1)}</p>}
            {r.open_issues?.map((o, i) => (
              <p key={i} className="text-xs text-[#C9A96E]" data-testid={`result-open-issue-${i}`}>Da chiarire: {o.problem} — {o.status}</p>
            ))}
          </div>
        ) : (
          <div className="mt-3"><span className="rounded-full bg-white px-1 py-1"><LevelBadge level={r.confidence} label="Attendibilità dell'analisi" testid="result-confidence" /></span></div>
        )}
        <p className="mt-3 text-xs text-[#F7F4EE]/80" data-testid="result-confidence-reason">{r.confidence_reason}</p>
      </div>

      <Block icon={Eye} title="Cosa osservo" testid="result-observed">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[#5b6b60]">Osservato nelle fotografie</p>
        {r.observed.length ? <List items={r.observed.map((o) => `${o.text}${o.photos?.length ? ` (foto ${o.photos.join(", ")})` : ""}`)} /> : <p className="text-sm text-[#5b6b60]">Nessun sintomo chiaramente osservabile.</p>}
        {r.user_declared?.length > 0 && (
          <>
            <p className="mb-2 mt-4 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-[#5b6b60]"><MessageSquareQuote size={12} /> Dichiarato da te</p>
            <List items={r.user_declared.map((d) => `${d.question} ${d.answer}`)} />
          </>
        )}
        {r.inferences?.length > 0 && (
          <>
            <p className="mb-2 mt-4 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-[#5b6b60]"><Brain size={12} /> Ragionamento dell'AI (inferenza)</p>
            <List items={r.inferences} />
          </>
        )}
      </Block>

      <Block icon={Target} title="Causa più probabile" testid="result-main-cause" accent>
        {r.main_cause ? (
          <>
            <Tag cls="bg-[#C9A96E]/20 text-[#7a5d2a]">{r.main_cause.type_label}</Tag>
            <p className="mt-2 text-lg font-semibold text-[#0D2517]">{r.main_cause.name} <span className="botanical text-base font-normal text-[#B39257]">{r.main_cause.agent}</span></p>
            <p className="mt-1 text-sm text-[#2D3A30]">{r.main_cause.explanation}</p>
          </>
        ) : (
          <p className="text-sm font-semibold text-[#7f1d1d]" data-testid="insufficient-statement">{r.insufficient_statement}</p>
        )}
        {r.not_distinguishable && <p className="mt-3 rounded-lg bg-[#f8f3e9] p-3 text-xs text-[#2D3A30]">{r.not_distinguishable}</p>}
      </Block>

      {r.differentials.length > 0 && (
        <Block icon={ListChecks} title="Altre possibili cause" testid="result-differentials">
          <div className="space-y-3">
            {r.differentials.map((d) => (
              <div key={d.id} className="rounded-xl border border-[#ddc69c]/70 p-3">
                <p className="text-sm font-semibold">{d.name} <span className="botanical font-normal text-[#B39257]">{d.agent}</span></p>
                <p className="text-[11px] uppercase tracking-wide text-[#8a958d]">{d.type_label}</p>
                {d.for?.length > 0 && <p className="mt-1 text-xs text-[#2D3A30]"><strong>A favore:</strong> {d.for.join("; ")}</p>}
                {d.against?.length > 0 && <p className="text-xs text-[#2D3A30]"><strong>Contro:</strong> {d.against.join("; ")}</p>}
              </div>
            ))}
          </div>
        </Block>
      )}

      {r.out_of_kb.length > 0 && (
        <Block icon={AlertTriangle} title="Ipotesi da verificare" testid="result-out-of-kb">
          {r.out_of_kb.map((o, i) => (
            <div key={i} className="mb-2 rounded-xl border border-dashed border-[#D97706]/50 bg-[#D97706]/5 p-3">
              <p className="text-sm font-semibold">{o.name}</p>
              <p className="text-xs text-[#2D3A30]">{o.rationale}</p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-[#B45309]">{o.label}</p>
            </div>
          ))}
        </Block>
      )}

      <AnamnesisFactors factors={r.anamnesis_factors} />
      {r.checks.length > 0 && <Block icon={FileCheck2} title="Cosa controllare" testid="result-checks"><List items={r.checks} /></Block>}
      <DoNow doNow={r.do_now} practiceIssues={r.practice_issues} />
      <Management management={r.management} />
      <Phyto phyto={r.phyto} />
      <Safety safety={r.safety} phyto={r.phyto} />

      {r.further.length > 0 && (
        <Block icon={Microscope} title="Quando serve un approfondimento" testid="result-further">
          <div className="space-y-2">
            {r.further.map((f, i) => (
              <p key={i} className="text-sm"><strong>{FURTHER[f.type] || f.type}:</strong> {f.reason}</p>
            ))}
          </div>
        </Block>
      )}
      <p className="text-center text-[10px] text-[#8a958d]">Analisi generata il {fmtDate(r.at)} · AI preliminare, non sostituisce la diagnosi professionale.</p>
    </div>
  );
}
